import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

type ThemeMode = "system" | "light" | "dark";

interface ThemeContextValue {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  resolvedMode: "light" | "dark";
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function getSystemDark() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function readStoredMode(): ThemeMode {
  try {
    const v = localStorage.getItem("theme-mode");
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    // localStorage unavailable (private browsing) — fall through to default
  }
  return "system";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);
  const [resolvedMode, setResolvedMode] = useState<"light" | "dark">(() =>
    mode === "dark" ? "dark" : mode === "light" ? "light" : getSystemDark() ? "dark" : "light"
  );

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");

    function update() {
      setResolvedMode(
        mode === "dark" ? "dark" : mode === "light" ? "light" : media.matches ? "dark" : "light"
      );
    }

    update();
    if (mode === "system") {
      media.addEventListener("change", update);
      return () => media.removeEventListener("change", update);
    }
  }, [mode]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", resolvedMode === "dark");
  }, [resolvedMode]);

  function setMode(next: ThemeMode) {
    setModeState(next);
    try {
      localStorage.setItem("theme-mode", next);
    } catch {
      // localStorage unavailable — the in-memory mode still applies
    }
  }

  return (
    <ThemeContext.Provider value={{ mode, setMode, resolvedMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
